"use client";
import React from "react";
import Link from "next/link";
import { useCurrentLocale } from "next-i18n-router/client";

import Typography from "@/components/Typography/Typography";
import CustomAccordion from "@/components/CustomAccordion/CustomAccordion";
import AccordionButton from "@/components/AccordionButton/AccordionButton";
import CustomButton from "@/components/CustomButton/CustomButton";
import SplideCarousel from "@/components/SplideCarousel/SplideCarousel";
import CustomStructuredText from "@/components/CustomStructuredText/CustomStructuredText";
import CustomIconifyIcon from "@/components/CustomIconifyIcon/CustomIconifyIcon";

import {
  IRoomsAndMeetingBoxProps,
  IFeatures,
} from "@/interfaces/RoomsAndMeetingBox/RoomsAndMeetingBox";
import { getBookingEngineUrl } from "@/lib/utils";
import i18nConfig from "../../../i18nConfig";
import { Config } from "next-i18n-router/dist/types";

export default function RoomsAndMeetingBox({
  title,
  description,
  expandButtonLabel,
  bookingButton,
  verticalBooking,
  expandedContent,
  features,
  images,
  brochure,
}: IRoomsAndMeetingBoxProps) {
  const [expanded, setExpanded] = React.useState(false);
  const currentLocale = useCurrentLocale(i18nConfig as Config);
  const lang = currentLocale || i18nConfig.defaultLocale;

  return (
    <div className="flex flex-col">
      <div>
        <SplideCarousel
          overlayBreadcrumb={""}
          overlayTitle={""}
          overlaySubtitle={""}
          carouselItems={images}
          className={"aspect-[4/3] cmd:aspect-video"}
        />
      </div>
      <div className="border-2 border-colorPrimary-50 order-t-0 pt-4">
        <div className="flex flex-col gap-6 px-6 py-8 cmd:py-12 cmd:px-8 cmd:flex-row ">
          <div id="leftPartOnDesktop" className="space-y-6 cmd:w-1/2">
            <Typography variant="section-title" className="text-colorPrimary leading-10">
              {title}
            </Typography>
            <div className="flex gap-4 flex-wrap">
              {features.map((feature: IFeatures, index: number) => (
                <div key={index} className="flex items-center gap-2">
                  <CustomIconifyIcon name={feature.iconifyIcon} className="w-1.5625 h-1.5625" />
                  <Typography variant="breadcrumbs-bold" className="!font-bold !normal-case">
                    {feature.label}
                  </Typography>
                </div>
              ))}
            </div>
            <div className="hidden gap-2.5 cmd:flex">
              <AccordionButton
                value={expandButtonLabel}
                expanded={expanded}
                setExpanded={setExpanded}
                className="grow cxs:grow-0"
              />
              {bookingButton && verticalBooking && (
                <Link
                  href={getBookingEngineUrl(verticalBooking, lang)}
                  prefetch={false}
                  target="_blank"
                  rel="noreferrer"
                  className="roomsAndMeeting-booknow-button"
                >
                  <CustomButton
                    variant="accent"
                    value={bookingButton.label}
                    icon={bookingButton.iconifyIcon}
                    className="font-extrabold font-barlow bg-colorSecondary-400 hover:bg-colorSecondary-300"
                  />
                </Link>
              )}

              {brochure && (
                <div className="hidden cmd:block">
                  <Link
                    href={brochure.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="grow cxs:grow-0"
                  >
                    <CustomButton
                      variant="accent"
                      className="bg-colorSecondary-700"
                      value={brochure.title}
                    />
                  </Link>
                </div>
              )}
            </div>
          </div>
          <div className="cmd:w-1/2">
            <CustomStructuredText
              data={description.value}
              paragraphWrapper={(para, key) => (
                <Typography key={key} variant="paragraph-light" className="!text-base">
                  {para}
                </Typography>
              )}
            />
          </div>
          <div className="flex flex-wrap-reverse gap-2.5 cmd:hidden">
            <AccordionButton
              value={expandButtonLabel}
              expanded={expanded}
              setExpanded={setExpanded}
              className="flex-grow"
            />
            {bookingButton && verticalBooking && (
              <Link
                href={getBookingEngineUrl(verticalBooking, lang)}
                prefetch={false}
                target="_blank"
                rel="noreferrer"
                className="flex-grow roomsAndMeeting-booknow-button"
              >
                <CustomButton
                  variant="accent"
                  value={bookingButton.label}
                  icon={bookingButton.iconifyIcon}
                  className="w-full"
                />
              </Link>
            )}
            {brochure && (
              <div className="block flex-grow">
                <Link
                  href={brochure.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex flex-grow w-full"
                >
                  <CustomButton
                    variant="accent"
                    className="bg-colorSecondary-700 w-full"
                    value={brochure.title}
                  />
                </Link>
              </div>
            )}
          </div>
        </div>
        <div className="pl-6 cmd:pl-8 bg-colorSecondary-50">
          <CustomAccordion
            heading={expandedContent.value}
            contents={expandedContent.value}
            expanded={expanded}
          />
        </div>
      </div>
    </div>
  );
}
